import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { Box, Button, Stack, Typography } from '@mui/material'
import FormSelect from 'components/form-elements/FormSelect'
import { ShareFields } from 'features/Share/components/ShareFields'
import { ShareMemberOption, ShareType } from 'features/Share/models/shareForm'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import React, { useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Note } from 'types'
import FormInput from '../../form-elements/FormInput'
import { NoteFormValues, noteFormSchema, NoteType, noteTypeOptions } from './noteForm'

interface Props {
  values?: Note
  onSubmitHandler: SubmitHandler<NoteFormValues>
  removeDisabled: boolean
  onRemove: () => void
  /** Show the Note / Shared Note toggle and the inline sharing controls (create flow only). */
  isShareable?: boolean
  shareMemberOptions?: ShareMemberOption[]
  onNoteTypeChange?: (noteType: NoteType) => void
}

const NoteEditForm = (props: Props) => {
  const {
    values,
    onSubmitHandler,
    removeDisabled,
    onRemove,
    isShareable = false,
    shareMemberOptions = [],
    onNoteTypeChange,
  } = props

  const formSettings = {
    mode: 'onChange' as const,
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      subject: values?.subject || '',
      body: values?.body || '',
      link: values?.link || '',
      noteType: NoteType.PERSONAL,
      type: ShareType.GROUP,
      members: [],
      comments: '',
    },
  }
  const methods = useForm<NoteFormValues>(formSettings)

  const { handleSubmit, watch } = methods

  const bodyValue = watch('body')
  const bodyCharsLeft = useCharactersLeft(200, bodyValue?.length ?? 0)

  const noteTypeValue = watch('noteType')
  const isShared = isShareable && noteTypeValue === NoteType.SHARED

  useEffect(() => {
    onNoteTypeChange?.(noteTypeValue)
  }, [noteTypeValue, onNoteTypeChange])

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <Stack spacing={2}>
          {isShareable && (
            <FormSelect name="noteType" label="Note type" options={noteTypeOptions} />
          )}

          <FormInput label="Subject (max 50 characters)" required fullWidth name="subject" />

          <FormInput
            label="Note"
            required
            fullWidth
            name="body"
            multiline
            minRows={5}
            maxRows={12}
            helperText={bodyCharsLeft}
          />

          <FormInput label="Link to image or website" fullWidth name="link" />

          {isShared && <ShareFields options={shareMemberOptions} />}
        </Stack>

        {!isShareable && (
          <Typography variant="body2" color="text.secondary" mt={4}>
            To share, open in the timeline
          </Typography>
        )}

        <Stack direction="row" spacing={2} mt={isShareable ? 4 : 1}>
          <Button variant="contained" type="submit" sx={{ minWidth: 120 }}>
            Post
          </Button>
          {!removeDisabled && (
            <Button variant="outlined" color="error" sx={{ minWidth: 120 }} onClick={onRemove}>
              Delete
            </Button>
          )}
        </Stack>
      </Box>
    </FormProvider>
  )
}

export default React.memo(NoteEditForm)
